import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { cleanupInspectionStorage, safeSetInspectionItem } from '../utils/inspectionStorage';
import './Inspection.css';

function Inspection({ onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const isExistingInspection = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idFromUrl = urlParams.get('id');
    return Boolean(idFromUrl && idFromUrl !== 'new' && !isNaN(idFromUrl));
  }, []);

  const getDraftSessionId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const idFromUrl = urlParams.get('id');
    if (idFromUrl && idFromUrl !== 'new') {
      return idFromUrl;
    }

    return localStorage.getItem('inspectionSessionId') || `INS_${Date.now()}`;
  };

  const [sessionId, setSessionId] = useState(() => getDraftSessionId());
  
  // Save session ID for new inspections
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('id') || new URLSearchParams(window.location.search).get('id') === 'new') {
      safeSetInspectionItem('inspectionSessionId', sessionId);
    }
  }, [sessionId]);

  const [inspectionNumber, setInspectionNumber] = useState(localStorage.getItem(`inspection_${sessionId}_number`) || sessionId);
  const [inspectionDate, setInspectionDate] = useState(localStorage.getItem(`inspection_${sessionId}_date`) || new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(localStorage.getItem(`inspection_${sessionId}_summary`) || '');
  const [pictures, setPictures] = useState(() => {
    try {
      const stored = localStorage.getItem(`inspection_${sessionId}_pictures`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(localStorage.getItem(`inspection_${sessionId}_clientId`) || '');
  const [selectedVehicleId, setSelectedVehicleId] = useState(localStorage.getItem(`inspection_${sessionId}_vehicleId`) || '');
  const [viewingAttachment, setViewingAttachment] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [reportPdfPath, setReportPdfPath] = useState('');
  const [transferJobCardNumber, setTransferJobCardNumber] = useState('');
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [transferringReport, setTransferringReport] = useState(false);

  const displayDate = useMemo(() => {
    if (!inspectionDate) {
      return '';
    }

    const [year, month, day] = inspectionDate.split('-');
    return `${month}/${day}/${year}`;
  }, [inspectionDate]);

  const selectedClient = clients.find((client) => String(client.ClientId) === String(selectedClientId));
  const selectedVehicle = vehicles.find((vehicle) => String(vehicle.VehicleInfoId) === String(selectedVehicleId));

  const loadNextInspectionNumber = useCallback(async (vehicleInfoId) => {
    if (!vehicleInfoId || isExistingInspection()) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/inspections/next-number/${vehicleInfoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nextInspectionNumber = response.data.inspectionNumber;

      if (nextInspectionNumber) {
        setInspectionNumber(nextInspectionNumber);
        setSessionId(nextInspectionNumber);
        safeSetInspectionItem('inspectionSessionId', nextInspectionNumber);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to generate inspection number.');
    }
  }, [isExistingInspection]);

  const handleVehicleChange = (vehicleInfoId) => {
    setSelectedVehicleId(vehicleInfoId);
    const vehicle = vehicles.find((item) => String(item.VehicleInfoId) === String(vehicleInfoId));
    if (vehicle?.ClientId) {
      setSelectedClientId(String(vehicle.ClientId));
    } else {
      setSelectedClientId('');
    }
  };

  const compressImageFile = (file) => new Promise((resolve) => {
    if (!file.type?.startsWith('image/')) {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const maxSide = 1600;
        const ratio = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * ratio));
        canvas.height = Math.max(1, Math.round(image.height * ratio));

        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        resolve({
          name: file.name.replace(/\.[^.]+$/, '.jpg'),
          type: 'image/jpeg',
          size: Math.round((dataUrl.length * 3) / 4),
          dataUrl,
        });
      };
      image.onerror = () => resolve(null);
      image.src = reader.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

  const readPdfFile = (file) => new Promise((resolve) => {
    if (file.type !== 'application/pdf') {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      type: 'application/pdf',
      size: file.size,
      dataUrl: reader.result,
    });
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

  const fileToAttachment = (file) => {
    if (file.type?.startsWith('image/')) {
      return compressImageFile(file);
    }

    if (file.type === 'application/pdf') {
      return readPdfFile(file);
    }

    return Promise.resolve(null);
  };

  const isPdfAttachment = (attachment) => attachment?.type === 'application/pdf'
    || attachment?.dataUrl?.startsWith('data:application/pdf');

  const isImageAttachment = (attachment) => attachment?.type?.startsWith('image/')
    || attachment?.dataUrl?.startsWith('data:image')
    || (attachment?.dataUrl && attachment.dataUrl.startsWith('/uploads/'));

  // Get the full URL for a picture (either base64 data or file path)
  const getPictureUrl = (picture) => {
    if (!picture.dataUrl) return '';
    // If it's already a base64 data URL, return as is
    if (picture.dataUrl.startsWith('data:')) return picture.dataUrl;
    // If it's a file path, construct the full URL
    if (picture.dataUrl.startsWith('/uploads/')) {
      const baseURL = axios.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || '';
      return `${baseURL}${picture.dataUrl}`;
    }
    return picture.dataUrl;
  };

  useEffect(() => {
    const fetchInspection = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get('id');

      if (id && id !== 'new' && !isNaN(id)) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`/api/inspections/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = response.data.inspection;
          setReportPdfPath(data.reportPdfPath || '');

          if (!localStorage.getItem(`inspection_${id}_loaded`)) {
            setInspectionNumber(data.inspectionNumber || '');
            setInspectionDate(data.inspectionDate || '');
            setSelectedClientId(String(data.clientId || ''));
            setSelectedVehicleId(String(data.vehicleInfoId || ''));
            setSummary(data.summary || '');
            setPictures(data.pictures || []);

            if (data.damageImage) {
              safeSetInspectionItem(`inspection_${id}_damage_image`, data.damageImage);
              safeSetInspectionItem(`inspection_${id}_damage_canvas`, data.damageImage);
            }

            if (data.checklist && data.checklist.length > 0) {
              const formattedChecklist = {};
              data.checklist.forEach(item => {
                formattedChecklist[`${item.GroupName}::${item.ItemName}`] = {
                  status: item.Status || '',
                  details: item.Details || ''
                };
              });
              safeSetInspectionItem(`inspection_${id}_checklist`, JSON.stringify(formattedChecklist));
            }

            safeSetInspectionItem(`inspection_${id}_loaded`, 'true');
          }

          safeSetInspectionItem('inspectionId', id);
        } catch (error) {
          setMessage(error.response?.data?.message || 'Failed to load inspection data.');
        }
      }
    };
    
    fetchInspection();
  }, []);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const token = localStorage.getItem('token');
        const auth = { headers: { Authorization: `Bearer ${token}` } };
        const [clientRes, vehicleRes] = await Promise.all([
          axios.get('/api/clients', { ...auth, params: { pageSize: 100 } }),
          axios.get('/api/vehicles', { ...auth, params: { pageSize: 100 } }),
        ]);

        setClients(clientRes.data.clients || []);
        setVehicles(vehicleRes.data.vehicles || []);
      } catch (error) {
        setMessage(error.response?.data?.message || 'Failed to load clients and vehicles.');
      }
    };

    loadLookups();
  }, []);

  // Update linked client and next inspection number when vehicle is selected
  useEffect(() => {
    if (selectedVehicleId) {
      const vehicle = vehicles.find(v => String(v.VehicleInfoId) === String(selectedVehicleId));
      if (vehicle) {
        if (vehicle.ClientId) {
          setSelectedClientId(String(vehicle.ClientId));
        }
        loadNextInspectionNumber(selectedVehicleId);
      }
    }
  }, [loadNextInspectionNumber, selectedVehicleId, vehicles]);

  useEffect(() => {
    safeSetInspectionItem(`inspection_${sessionId}_clientId`, selectedClientId);
  }, [selectedClientId, sessionId]);

  useEffect(() => {
    safeSetInspectionItem(`inspection_${sessionId}_vehicleId`, selectedVehicleId);
  }, [selectedVehicleId, sessionId]);

  useEffect(() => {
    safeSetInspectionItem(`inspection_${sessionId}_number`, inspectionNumber);
  }, [inspectionNumber, sessionId]);

  useEffect(() => {
    safeSetInspectionItem(`inspection_${sessionId}_date`, inspectionDate);
  }, [inspectionDate, sessionId]);

  useEffect(() => {
    safeSetInspectionItem(`inspection_${sessionId}_summary`, summary);
  }, [summary, sessionId]);

  useEffect(() => {
    try {
      safeSetInspectionItem(`inspection_${sessionId}_pictures`, JSON.stringify(pictures));
    } catch (e) {
      console.warn('Failed to save pictures to local storage:', e);
    }
  }, [pictures, sessionId]);

  const handleFiles = async (files) => {
    const incomingFiles = Array.from(files || []);
    const supportedFiles = incomingFiles.filter((file) => (
      file.type?.startsWith('image/') || file.type === 'application/pdf'
    ));
    const remainingSlots = Math.max(50 - pictures.length, 0);
    const selectedFiles = supportedFiles.slice(0, Math.min(10, remainingSlots));

    if (remainingSlots <= 0) {
      setMessage('50 files maximum reached.');
      return;
    }

    if (supportedFiles.length !== incomingFiles.length) {
      setMessage('Only image and PDF files are supported.');
    } else if (supportedFiles.length > selectedFiles.length) {
      setMessage(`Added ${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'}. Maximum 10 per batch and 50 total.`);
    } else {
      setMessage('');
    }

    const nextPictures = (await Promise.all(selectedFiles.map(fileToAttachment))).filter(Boolean);
    setPictures((current) => [...current, ...nextPictures]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const removePicture = (index) => {
    setPictures((current) => current.filter((_, pictureIndex) => pictureIndex !== index));
  };

  const openAttachment = (attachment) => {
    if (!attachment?.dataUrl) {
      setMessage('Attachment preview is not available.');
      return;
    }

    setViewingAttachment(attachment);
  };

  const checklistItems = useMemo(() => {
    try {
      const savedChecklist = JSON.parse(localStorage.getItem(`inspection_${sessionId}_checklist`) || '{}');
      return Object.entries(savedChecklist).map(([key, value]) => {
        const [groupName, itemName] = key.split('::');
        return {
          groupName,
          itemName,
          status: value.status || '',
          details: value.details || '',
        };
      });
    } catch (error) {
      return [];
    }
  }, [sessionId]);
  const checklistSummary = useMemo(() => checklistItems.reduce((summaryItems, item) => {
    const status = String(item.status || '').trim().toLowerCase();
    summaryItems.total += 1;
    if (status === 'good') summaryItems.good += 1;
    else if (status === 'fair') summaryItems.fair += 1;
    else if (status === 'poor') summaryItems.poor += 1;
    else summaryItems.unset += 1;
    return summaryItems;
  }, {
    total: 0,
    good: 0,
    fair: 0,
    poor: 0,
    unset: 0,
  }), [checklistItems]);

  const saveInspection = async () => {
    if (!inspectionNumber.trim()) {
      setMessage('Inspection number is required.');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        inspectionId: localStorage.getItem('inspectionId') || null,
        inspectionNumber,
        inspectionDate,
        clientId: selectedClientId || null,
        vehicleInfoId: selectedVehicleId || null,
        summary,
        damageImage: localStorage.getItem(`inspection_${sessionId}_damage_image`) || null,
        checklist: checklistItems,
        pictures,
      };
      
      console.log('Saving inspection, payload size:', JSON.stringify(payload).length, 'bytes');
      
      const response = await axios.post('/api/inspections', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 120000, // 2 minute timeout for large image uploads
      });

      safeSetInspectionItem('inspectionId', response.data.inspectionId);
      setReportPdfPath(response.data.reportPdfPath || '');
      setMessage(`Inspection #${inspectionNumber} saved.`);
      
      // Clear session-specific data after successful save
      const keysToRemove = [
        `inspection_${sessionId}_pictures`,
        `inspection_${sessionId}_checklist`,
        `inspection_${sessionId}_damage_image`,
        `inspection_${sessionId}_damage_canvas`,
        `inspection_${sessionId}_clientId`,
        `inspection_${sessionId}_vehicleId`,
        `inspection_${sessionId}_number`,
        `inspection_${sessionId}_date`,
        `inspection_${sessionId}_summary`,
        `inspection_${sessionId}_loaded`,
        'inspectionSessionId'
      ];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      cleanupInspectionStorage();
      
      navigate('/inspection');
    } catch (error) {
      console.error('Save inspection error:', error);
      let errorMessage = 'Failed to save inspection.';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. The images may be too large.';
      } else if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      setMessage(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const downloadReportPdf = async () => {
    const inspectionId = localStorage.getItem('inspectionId');
    if (!inspectionId) {
      setMessage('Save the inspection first to generate the report PDF.');
      return;
    }

    setDownloadingReport(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/inspections/${inspectionId}/report-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${inspectionNumber || 'inspection'}-report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
      setMessage('Inspection report PDF generated.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to generate inspection report PDF.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const transferReportToJobCard = async () => {
    const inspectionId = localStorage.getItem('inspectionId');
    if (!inspectionId) {
      setMessage('Save the inspection first before transferring the report.');
      return;
    }

    if (!transferJobCardNumber.trim()) {
      setMessage('Enter a job card number to transfer the report.');
      return;
    }

    setTransferringReport(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/inspections/${inspectionId}/transfer-to-jobcard`, {
        jobCardNumber: transferJobCardNumber.trim(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`Inspection report transferred to job card ${transferJobCardNumber.trim()}.`);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to transfer report to job card.');
    } finally {
      setTransferringReport(false);
    }
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="inspection-page">
        <header className="inspection-header">
          <button className="app-back-button inspection-back-button" type="button" onClick={() => navigate('/inspection')} aria-label="Back to inspections">
            Back
          </button>
          <div className="inspection-header-copy">
            <h1>Inspection</h1>
            <p>Create a thorough vehicle inspection by filling in the checklist</p>
          </div>
        </header>

        <form className="inspection-card">
          <section className="inspection-section">
            <div className="inspection-section-heading">
              <h2>Vehicle Inspection</h2>
              <p>Choose the customer and vehicle, then continue with checklist and damage review.</p>
            </div>

            <div className="inspection-field-grid">
              <label>
                Inspection number
                <input
                  type="text"
                  value={inspectionNumber}
                  onChange={(event) => setInspectionNumber(event.target.value)}
                />
              </label>

              <label>
                Inspection date
                <span className="inspection-date-wrap">
                  <input
                    type="date"
                    value={inspectionDate}
                    onChange={(event) => setInspectionDate(event.target.value)}
                  />
                  <span aria-hidden="true">{displayDate}</span>
                </span>
              </label>
            </div>

            <div className="inspection-action-grid">
              <div className="inspection-tile client-tile">
                <span className="inspection-tile-icon" aria-hidden="true">👨‍👩‍👧‍👦</span>
                <label>
                  <strong>Client</strong>
                  <select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)} disabled={Boolean(selectedVehicle?.ClientId)}>
                    <option value="">{selectedVehicleId ? 'No linked client found' : 'Select car number first'}</option>
                    {clients.map((client) => (
                      <option key={client.ClientId} value={client.ClientId}>
                        {client.ClientName} {client.Phone ? `- ${client.Phone}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="inspection-plus" aria-hidden="true">+</span>
              </div>

              <div className="inspection-tile vehicle-tile">
                <span className="inspection-tile-icon" aria-hidden="true">🚙</span>
                <label>
                  <strong>Car Number</strong>
                  <select value={selectedVehicleId} onChange={(event) => handleVehicleChange(event.target.value)}>
                    <option value="">Select car number</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.VehicleInfoId} value={vehicle.VehicleInfoId}>
                        {[vehicle.CarNumber, vehicle.Year, vehicle.Make, vehicle.Model].filter(Boolean).join(' ')}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="inspection-plus" aria-hidden="true">+</span>
              </div>

              <button className="inspection-tile checklist-tile" type="button" onClick={() => navigate(`/inspection/checklist?id=${sessionId}`)}>
                <span className="inspection-tile-icon" aria-hidden="true">📋</span>
                <strong>Checklist</strong>
              </button>

              <button className="inspection-tile damage-tile" type="button" onClick={() => navigate(`/inspection/damage?id=${sessionId}`)}>
                <span className="inspection-tile-icon" aria-hidden="true">🏗️</span>
                <strong>Damage Report</strong>
              </button>
            </div>

            <div className="inspection-selected-strip">
              <span>{selectedClient ? `Client: ${selectedClient.ClientName}` : 'No client selected'}</span>
              <span>
                {selectedVehicle
                  ? `Vehicle: ${[selectedVehicle.CarNumber, selectedVehicle.Make, selectedVehicle.Model].filter(Boolean).join(' ')}`
                  : 'No vehicle selected'}
              </span>
            </div>
          </section>

          <section className="inspection-section report-section">
            <div className="inspection-section-heading">
              <h2>Report Studio</h2>
              <p>A polished inspection report PDF is created from the checklist, damage report, summary, uploaded images, and uploaded PDF files.</p>
            </div>

            <div className="inspection-report-hero">
              <div className="inspection-report-copy">
                <span className="inspection-report-kicker">Inspection Output</span>
                <h3>{inspectionNumber || 'Inspection Report'}</h3>
                <p>The report bundles the inspection checklist, marked damage image, uploaded evidence, and your final review into one PDF ready for workshop handoff.</p>
              </div>
              <div className="inspection-report-stats">
                <div><strong>{checklistSummary.total}</strong><span>Checklist Items</span></div>
                <div><strong>{pictures.length}</strong><span>Files Included</span></div>
                <div><strong>{checklistSummary.poor}</strong><span>Poor Findings</span></div>
              </div>
            </div>

            <div className="inspection-report-grid">
              <article className="inspection-report-card">
                <h3>Checklist Snapshot</h3>
                <div className="inspection-report-metrics">
                  <span>Good {checklistSummary.good}</span>
                  <span>Fair {checklistSummary.fair}</span>
                  <span>Poor {checklistSummary.poor}</span>
                  <span>Unmarked {checklistSummary.unset}</span>
                </div>
              </article>

              <article className="inspection-report-card">
                <h3>Damage & Evidence</h3>
                <p>{localStorage.getItem(`inspection_${sessionId}_damage_image`) ? 'Damage markup included in report.' : 'No saved damage markup yet.'}</p>
                <p>{pictures.filter(isImageAttachment).length} image file(s), {pictures.filter(isPdfAttachment).length} PDF file(s)</p>
              </article>

              <article className="inspection-report-card">
                <h3>Job Card Transfer</h3>
                <p>After saving, move the PDF into a job card attachment using the job card number. The PDF will appear in the job card upload section.</p>
              </article>
            </div>

            <div className="inspection-report-actions">
              <button type="button" className="inspection-report-button" onClick={downloadReportPdf} disabled={downloadingReport || saving}>
                {downloadingReport ? 'Preparing PDF...' : 'Download Report PDF'}
              </button>
              {reportPdfPath ? (
                <a
                  className="inspection-report-link"
                  href={`${axios.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || ''}${reportPdfPath}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Current PDF
                </a>
              ) : (
                <span className="inspection-report-link muted">Save inspection to create current PDF</span>
              )}
            </div>

            <div className="inspection-transfer-panel">
              <label>
                Transfer to Job Card #
                <input
                  type="text"
                  value={transferJobCardNumber}
                  onChange={(event) => setTransferJobCardNumber(event.target.value)}
                  placeholder="Enter job card number"
                />
              </label>
              <button type="button" className="inspection-transfer-button" onClick={transferReportToJobCard} disabled={transferringReport || saving}>
                {transferringReport ? 'Transferring...' : 'Transfer Report to Job Card'}
              </button>
            </div>
          </section>

          <section className="inspection-section upload-section">
            <div className="inspection-section-title">
              <h2>Upload Pictures / PDF</h2>
              <button type="button" className="camera-button" onClick={openFilePicker} aria-label="Upload pictures or PDF files">
                <span aria-hidden="true">▣</span>
              </button>
            </div>

            <div
              className="inspection-dropzone"
              onClick={openFilePicker}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                handleFiles(event.dataTransfer.files);
              }}
              role="button"
              tabIndex="0"
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  openFilePicker();
                }
              }}
            >
              <span className="upload-cloud" aria-hidden="true">⌂</span>
              <p><strong>Click to upload</strong> or drag and drop</p>
              <small>eg. vehicle images, damage reports, scratches, PDF reports, etc.</small>
              <small>50 files limit (10 per batch)</small>
              {pictures.length > 0 && <em>{pictures.length} file{pictures.length === 1 ? '' : 's'} selected</em>}
            </div>

            {pictures.length > 0 && (
              <div className="inspection-picture-grid">
                {pictures.map((picture, index) => (
                  <figure key={`${picture.name}-${index}`} className="inspection-picture-preview">
                    <button
                      type="button"
                      className="inspection-attachment-open"
                      onClick={() => openAttachment(picture)}
                      aria-label={`View ${picture.name || 'attachment'}`}
                    >
                      {isImageAttachment(picture) && getPictureUrl(picture) ? (
                        <img src={getPictureUrl(picture)} alt={picture.name || `Inspection ${index + 1}`} />
                      ) : isPdfAttachment(picture) ? (
                        <span className="inspection-pdf-preview">PDF</span>
                      ) : (
                        <span className="inspection-picture-missing">No preview</span>
                      )}
                    </button>
                    <figcaption title={picture.name || ''}>{picture.name || `Image ${index + 1}`}</figcaption>
                    <button type="button" className="inspection-attachment-remove" onClick={() => removePicture(index)} aria-label={`Remove ${picture.name || 'attachment'}`}>
                      Remove
                    </button>
                  </figure>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              className="inspection-file-input"
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={(event) => handleFiles(event.target.files)}
            />
          </section>

          <section className="inspection-section summary-section">
            <h2>Summary</h2>
            <label>
              Write a final review based on your findings. This will be visible on printed inspection review
              <textarea
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                placeholder="eg. The vehicle is in no driving condition. We recommend immediate repair."
              />
            </label>
          </section>

          <button className="inspection-save" type="button" onClick={saveInspection} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          {message && <div className="inspection-message">{message}</div>}
        </form>

        {viewingAttachment && (
          <div className="inspection-viewer" role="dialog" aria-modal="true">
            <div className="inspection-viewer-header">
              <strong>{viewingAttachment.name || 'Attachment'}</strong>
              <button type="button" onClick={() => setViewingAttachment(null)}>Close</button>
            </div>
            <div className="inspection-viewer-body">
              {isPdfAttachment(viewingAttachment) ? (
                <iframe src={getPictureUrl(viewingAttachment)} title={viewingAttachment.name || 'PDF attachment'} />
              ) : (
                <img src={getPictureUrl(viewingAttachment)} alt={viewingAttachment.name || 'Inspection attachment'} />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Inspection;
